/**
 * 業務の業務ロジック（`getBusinessRules` の応答）から、ロジック図の材料を組む。
 *
 * 規則は材料の値だけで決まり、同じ応答からは毎回同じ図になる（CLAUDE.md「決定論を優先する」）。
 *
 * 節点は Figma の図と 1 対 1 にする（Dhik 2026-10-01「図よりノードを増やすな」）。業務ルールの中身の表は
 * 図に入れず、業務ルールの箱を押して開く。
 *
 * - **分岐**: 結末を決める業務ルール（判定表で、行の出力に結末の結果の値を持つもの。制約 67 で業務に
 *   1 つ）は業務ルールの箱にしない。上から先に当たった行を採る判定表では、前の行に覆われて到達しない
 *   行は描かない（`reachableRowsOf`）。入力の列 1 つにつき分岐 1 つに開き、行を上から順に、最初に値を
 *   見る列の分岐から「セルの値」のラベルで行き先（次に値を見る列の分岐か、行の出力の結末）へ線を
 *   引く。どの行にも当たらなかった流れは次の列の分岐へ渡す。次の分岐が読む値を業務の中で作るなら、
 *   その値を作る業務ルールの連なりの先頭へ渡す（図の「選定途中のユーザーがいるか →いない→ 選定可能な
 *   ユーザーの判定をする」）。ラベルは、分岐に畳んだ判定表なら見ていない値（いない）、それ以外は「それ以外」
 *   業務の出力を返す結末へ向かう線は、その分岐の値を読んで業務の出力を作る業務ルールの連なり（並べる →
 *   上位 1 件を取得する）を通す（図の「選定可能なユーザーリストの数 →1 件以上→ 選定可能なユーザーを並べる」）
 * - **分岐に畳む業務ルール**: 出力を結末を決める判定表だけが読む判定表は、分岐の問いそのもの（「選定途中の
 *   ユーザーがいるか」）なので箱にしない。その業務ルールが読む値を、分岐の入力として直に繋ぐ
 * - **業務ルール**: 変更（mutation）と分岐に畳むもの以外の業務ルール。並べた先頭から N 件を出す並べ替え
 *   （`take`）は、並べ替え → 並べた結果 → 「上位 N 件を取得する」→ 出力 の順に箱を置く
 * - **業務ルールの出力**: 業務ルールの出力のうち、別の業務ルールか分岐が読むもの
 * - **入力**: 業務ルール（か分岐）が読む値のうち、業務の中のどの業務ルールも出さないもの。読む側 1 つに
 *   箱 1 つへ束ねる
 * - **作用**: 変更（mutation）の業務ルール。変える先の値を到達の印に持つ結末の直前に置く。印だけ
 *   持つ結末（変更の業務ルールが無い）は、印そのものを作用として置く
 * - **終了**: 結末 1 つにつき 1 つ。遷移先業務（業務フローの枝の宛先）と返り値（業務の出す区分。
 *   止まる ・ 戻る結末はエラー）を中の行に並べる
 * - **段**: 見本 v2 と同じ並びにする（Dhik 2026-10-01「v2 はできてるやん。これのマッチ度の算出を抜いた
 *   形よ」）。左から右へ進み、分岐の先へ続く流れは 1 段下へ、分岐から直に出る終了は 1 段上へ置く
 *   （`flowLanesOf`）
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務の業務ルール・結末・次の業務から、分岐・業務ルール・業務ルールの出力・入力・作用・終了の節点と線を決定論で組む。
 * @graph-connects none
 */
import type {
  BusinessRuleView,
  DecisionTableCellView,
  GetBusinessRulesOutput,
  RuleBodyView,
} from '@cortex/domain-spec-console';

import { getOr, type LogicDiagramModel, type LogicEdge, type LogicNode } from './logic-model.js';

/**
 * 図に出す文言。訳は画面が引いて渡す。
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 実データのロジック図に出す文言（表示名・それ以外・終了・遷移先・返り値・分岐の条件のセル）を表す。
 * @graph-connects none
 */
export interface LogicFromRulesTexts {
  /** 用語の表示名（未翻訳の印を含む） */
  readonly label: (value: string, untranslated: boolean) => string;
  /** どの行にも当たらなかった流れの線のラベル */
  readonly otherwise: string;
  /** 並べた先頭から N 件を取り出す箱の名前 */
  readonly take: (count: number) => string;
  /** 終了の名前（結末の値を受ける） */
  readonly endTitle: (result: string) => string;
  /** 遷移先業務の行 */
  readonly nextBusiness: (business: string) => string;
  /** 止まる結末の遷移先の行 */
  readonly stop: string;
  /** ステージの出口へ出る結末の遷移先の行 */
  readonly exit: string;
  /** 戻る結末の遷移先の行 */
  readonly back: (business: string) => string;
  /** 返り値の行 */
  readonly returns: (value: string) => string;
  /** エラーの返り値の行（結末の値を受ける） */
  readonly errorReturns: (result: string) => string;
  /** 印だけの作用の名前 */
  readonly markEffect: (property: string, value: string) => string;
  /** 見ない欄のセル */
  readonly any: string;
  /** 範囲のセル（比べ方 ・ 相手） */
  readonly range: (comparator: string, operand: string) => string;
}

/** 表示名と未翻訳の印を持つもの（用語の参照・業務ルール）。 @graph-connects none */
interface Labeled {
  readonly label: string;
  readonly untranslated: boolean;
}

/** 番号と表示名を持つ参照。 @graph-connects none */
interface NamedRef extends Labeled {
  readonly id: number;
}

/** 組み立て中の仮の段。段は組み終えてから `flowLanesOf` が線の流れで決め直す。 @graph-connects none */
const DRAFT_LANE = 0;

/** 業務ルールの節点の鍵。 @graph-connects none */
export function logicRuleKey(ruleId: number): string {
  return `rule:${String(ruleId)}`;
}

/** 業務ルールの出力の節点の鍵（出力の用語で 1 つ）。 @graph-connects none */
function outputKey(termId: number): string {
  return `out:${String(termId)}`;
}

/** 分岐の節点の鍵（結末を決める判定表の入力の列で 1 つ）。 @graph-connects none */
function branchKey(termId: number): string {
  return `branch:${String(termId)}`;
}

/** 終了の節点の鍵（結末の値で 1 つ）。 @graph-connects none */
function endKey(resultId: number): string {
  return `end:${String(resultId)}`;
}

/**
 * 結末を決める業務ルール。判定表で、行の出力に結末の結果の値を持つもの（業務に 1 つ）。
 * @graph-connects none
 */
function decidingRuleOf(logic: GetBusinessRulesOutput): BusinessRuleView | null {
  const results = new Set(logic.endings.map((ending) => ending.result.id));
  return (
    logic.rules.find(
      (rule) =>
        rule.body.kind === 'decisionTable' &&
        rule.body.rows.some((row) => row.outputs.some((output) => results.has(output.id))),
    ) ?? null
  );
}

/** 判定表のセルの文字。 @graph-connects none */
function cellText(cell: DecisionTableCellView, texts: LogicFromRulesTexts): string {
  if (cell === null) return texts.any;
  if ('comparator' in cell) {
    const operand =
      typeof cell.operand === 'number'
        ? String(cell.operand)
        : texts.label(cell.operand.label, cell.operand.untranslated);
    return texts.range(cell.comparator, operand);
  }
  return texts.label(cell.label, cell.untranslated);
}

/** 判定表の中身。 @graph-connects none */
type DecisionTableBody = Extract<RuleBodyView, { kind: 'decisionTable' }>;

/** 判定表の行。 @graph-connects none */
type DecisionTableRow = DecisionTableBody['rows'][number];

/**
 * 前の行が、この行より広い条件で先に当たるか。前の行が見る列をすべて、この行も同じ値で見ていれば、
 * この行に当たる入力は必ず前の行にも当たる。どの列も見ない前の行は、後ろの行を全部覆う。
 * @graph-connects none
 */
function coversRow(earlier: DecisionTableRow, row: DecisionTableRow): boolean {
  return earlier.cells.every(
    (cell, index) => cell === null || JSON.stringify(cell) === JSON.stringify(row.cells[index]),
  );
}

/**
 * 判定表の行のうち、到達できる行。上から先に当たった行を採る判定表（`firstMatch`）では、前の行に
 * 覆われた行には到達しないので描かない（描くと、起きない行き先が「それ以外」として並ぶ）。
 * 重ならない判定表（`none`）は行が互いに排他なので、全部の行を描く。
 *
 * @param body 判定表の中身
 * @returns 到達できる行（元の並び）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 上から先に当たった行を採る判定表で、前の行に覆われて到達しない行を除き、図に描く行を決める。
 * @graph-connects coversRow [calls] 前の行が先に当たるかの判定
 */
export function reachableRowsOf(body: DecisionTableBody): DecisionTableRow[] {
  if (body.overlapPolicy !== 'firstMatch') return [...body.rows];
  return body.rows.filter(
    (row, index) => !body.rows.slice(0, index).some((earlier) => coversRow(earlier, row)),
  );
}

/**
 * 分岐に畳む業務ルール（分岐の列の用語 → 業務ルール）。出力を結末を決める判定表だけが読む判定表は、
 * 分岐の問いそのものなので箱にしない（図では「選定途中のユーザーの情報 → 選定途中のユーザーがいるか」と
 * 入力から分岐へ直に繋がる）。ほかの業務ルールも読む出力は、途中のデータなので畳まない。
 *
 * @param logic `getBusinessRules` の応答
 * @param deciding 結末を決める業務ルール
 * @returns 分岐の列の用語の番号 → 畳む業務ルール
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 出力を結末を決める判定表だけが読む判定表を、ロジック図で分岐に畳む業務ルールとして選ぶ。
 * @graph-connects none
 */
export function foldedIntoBranchesOf(
  logic: GetBusinessRulesOutput,
  deciding: BusinessRuleView | null,
): ReadonlyMap<number, BusinessRuleView> {
  const folded = new Map<number, BusinessRuleView>();
  if (deciding === null || deciding.body.kind !== 'decisionTable') return folded;
  const columns = new Set(deciding.body.inputs.map((column) => column.id));
  for (const rule of logic.rules) {
    if (rule === deciding || rule.body.kind !== 'decisionTable' || rule.output === null) continue;
    const outputId = rule.output.id;
    if (!columns.has(outputId)) continue;
    const readElsewhere = logic.rules.some(
      (other) =>
        other !== deciding &&
        other !== rule &&
        other.inputs.some((input) => input.term.id === outputId),
    );
    if (!readElsewhere) folded.set(outputId, rule);
  }
  return folded;
}

/**
 * 線の流れから節点の段を決める（見本 v2 と同じ並び）。左から右へ進み、**分岐の先へ続く流れは 1 段下**へ
 * 下ろす。分岐から直に出る終了はその分岐の 1 段上（0 より上には出さない）、入力は使う箱と同じ段に置く。
 *
 * @param nodes 節点
 * @param edges 線
 * @returns 節点の鍵 → 段
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ロジック図の節点の段を、分岐を越えるたびに 1 段下がる流れで決める（分岐から直に出る終了は 1 段上、入力は使う箱と同じ段）。
 * @graph-connects none
 */
export function flowLanesOf(
  nodes: readonly LogicNode[],
  edges: readonly LogicEdge[],
): ReadonlyMap<string, number> {
  const kindOf = new Map(nodes.map((node) => [node.key, node.kind]));
  const preds = new Map<string, string[]>();
  for (const edge of edges) {
    if (kindOf.get(edge.from) === 'input') continue;
    preds.set(edge.to, [...getOr(preds, edge.to, []), edge.from]);
  }
  const memo = new Map<string, number>();
  const segmentOf = (key: string, guard: number): number => {
    const known = memo.get(key);
    if (known !== undefined) return known;
    const from = guard >= nodes.length ? [] : getOr(preds, key, []);
    const isEnd = kindOf.get(key) === 'end';
    const lane = Math.max(
      0,
      ...from.map((pred) => {
        const base = segmentOf(pred, guard + 1);
        if (kindOf.get(pred) !== 'branch') return base;
        return isEnd ? Math.max(0, base - 1) : base + 1;
      }),
    );
    memo.set(key, lane);
    return lane;
  };
  const lanes = new Map<string, number>();
  for (const node of nodes) {
    if (node.kind === 'input') continue;
    lanes.set(node.key, segmentOf(node.key, 0));
  }
  for (const node of nodes) {
    if (node.kind !== 'input') continue;
    const consumer = edges.find((edge) => edge.from === node.key)?.to;
    lanes.set(node.key, consumer === undefined ? 0 : getOr(lanes, consumer, 0));
  }
  return lanes;
}

/**
 * 業務の業務ロジックからロジック図の材料を組む。
 *
 * @param logic `getBusinessRules` の応答
 * @param texts 図に出す文言
 * @returns ロジック図の材料
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務の業務ロジックから、結末を決める判定表を分岐に開き（分岐だけが読む判定表は分岐に畳み）、業務ルール・出力・入力・作用・終了の節点と線を組む。
 * @graph-connects decidingRuleOf [calls] 結末を決める業務ルール
 * @graph-connects foldedIntoBranchesOf [calls] 分岐に畳む業務ルール
 * @graph-connects flowLanesOf [calls] 線の流れから段を決める
 */
export function buildLogicModelFromRules(
  logic: GetBusinessRulesOutput,
  texts: LogicFromRulesTexts,
): LogicDiagramModel {
  const name = (ref: Labeled): string => texts.label(ref.label, ref.untranslated);
  const nodes: LogicNode[] = [];
  const edges: LogicEdge[] = [];
  const seen = new Set<string>();
  const addNode = (node: LogicNode): void => {
    if (seen.has(node.key)) return;
    seen.add(node.key);
    nodes.push(node);
  };
  const addEdge = (edge: LogicEdge): void => {
    if (
      edges.some(
        (other) => other.from === edge.from && other.to === edge.to && other.label === edge.label,
      )
    )
      return;
    edges.push(edge);
  };

  const deciding = decidingRuleOf(logic);
  const mutations = logic.rules.filter((rule) => rule.body.kind === 'mutation');
  const folded = foldedIntoBranchesOf(logic, deciding);
  const workers = logic.rules.filter(
    (rule) =>
      rule.body.kind !== 'mutation' && rule !== deciding && ![...folded.values()].includes(rule),
  );
  /* 業務の中で値を出す業務ルール（出力の用語 → 業務ルール）。 */
  const producers = new Map<number, BusinessRuleView>();
  for (const rule of workers) if (rule.output !== null) producers.set(rule.output.id, rule);

  /* 読む側（業務ルールか分岐）の鍵と、その読む値の並びから、線と入力の束を足す。 */
  const connectReads = (readerKey: string, terms: readonly NamedRef[]): void => {
    const raw: NamedRef[] = [];
    for (const term of terms) {
      const producer = producers.get(term.id);
      if (producer === undefined) {
        raw.push(term);
        continue;
      }
      addEdge({ from: outputKey(term.id), to: readerKey });
    }
    if (raw.length === 0) return;
    const inputKey = `in:${readerKey}`;
    addNode({
      key: inputKey,
      kind: 'input',
      title: '',
      lines: raw.map(name),
      lane: DRAFT_LANE,
    });
    addEdge({ from: inputKey, to: readerKey });
  };

  for (const rule of workers) {
    const key = logicRuleKey(rule.ruleId);
    addNode({
      key,
      kind: 'rule',
      title: name(rule),
      lines: [],
      lane: DRAFT_LANE,
      ruleId: rule.ruleId,
    });
    connectReads(
      key,
      rule.inputs.map((input) => input.term),
    );
    if (rule.output !== null) {
      addNode({
        key: outputKey(rule.output.id),
        kind: 'ruleOutput',
        title: name(rule.output),
        lines: [],
        lane: DRAFT_LANE,
      });
      /*
       * 並べた先頭から N 件を出す並べ替えは、並べ替え → 並べた結果 → 取り出し → 出力 の順に置く
       * （図の「並べる → 選定可能なユーザーリスト → 上位 1 件を取得する → 選定可能なユーザーリスト」）。
       */
      const take = rule.body.kind === 'sort' ? rule.body.take : null;
      if (take === null) {
        addEdge({ from: key, to: outputKey(rule.output.id) });
      } else {
        const sortedKey = `sorted:${String(rule.ruleId)}`;
        const takeKey = `take:${String(rule.ruleId)}`;
        addNode({
          key: sortedKey,
          kind: 'ruleOutput',
          title: name(rule.output),
          lines: [],
          lane: DRAFT_LANE,
        });
        addNode({
          key: takeKey,
          kind: 'rule',
          title: texts.take(take),
          lines: [],
          lane: DRAFT_LANE,
          ruleId: rule.ruleId,
        });
        addEdge({ from: key, to: sortedKey });
        addEdge({ from: sortedKey, to: takeKey });
        addEdge({ from: takeKey, to: outputKey(rule.output.id) });
      }
    }
  }

  /* 終了。遷移先と返り値を中の行に並べる。 */
  const errorKinds = new Set(['stop', 'back']);
  /* 止まる ・ 戻るだけの結末（業務の出力を返さない）。 */
  const errorResults = new Set<number>();
  for (const ending of logic.endings) {
    const routes = logic.nextBusinesses.filter((next) => next.result?.id === ending.result.id);
    const destinations = routes.map((route) =>
      route.destinationKind === 'stop'
        ? texts.stop
        : route.destinationKind === 'exit'
          ? texts.exit
          : route.destinationKind === 'back'
            ? texts.back(route.target === null ? '' : name(route.target))
            : texts.nextBusiness(route.target === null ? '' : name(route.target)),
    );
    const isError =
      routes.length > 0 && routes.every((route) => errorKinds.has(route.destinationKind));
    if (isError) errorResults.add(ending.result.id);
    const returns = isError
      ? texts.errorReturns(name(ending.result))
      : logic.output.class === null
        ? null
        : texts.returns(name(logic.output.class));
    addNode({
      key: endKey(ending.result.id),
      kind: 'end',
      title: texts.endTitle(name(ending.result)),
      lines: [...new Set(destinations), ...(returns === null ? [] : [returns])],
      lane: DRAFT_LANE,
    });
  }

  /*
   * 結末の手前の作用。変える先の値を到達の印に持つ結末の前に変更の業務ルールを置き、印だけの
   * 結末は印を作用として置く。結末へ入る線は、作用があれば作用の先頭へ繋ぎ替える。
   */
  const entryOf = new Map<number, string>();
  for (const ending of logic.endings) {
    const mark = ending.reachedMark;
    const chain: string[] = [];
    if (mark !== null) {
      const matching = mutations.filter(
        (rule) => rule.body.kind === 'mutation' && rule.body.to?.id === mark.value.id,
      );
      for (const rule of matching) {
        const key = logicRuleKey(rule.ruleId);
        addNode({
          key,
          kind: 'effect',
          title: name(rule),
          lines: [],
          lane: DRAFT_LANE,
          ruleId: rule.ruleId,
        });
        chain.push(key);
      }
      if (matching.length === 0) {
        const key = `mark:${String(ending.result.id)}`;
        addNode({
          key,
          kind: 'effect',
          title: texts.markEffect(name(mark.property), name(mark.value)),
          lines: [],
          lane: DRAFT_LANE,
        });
        chain.push(key);
      }
    }
    /* 後ろから繋ぎ、最後に残った先頭をその結末の入口にする。 */
    let entry = endKey(ending.result.id);
    for (const key of [...chain].reverse()) {
      addEdge({ from: key, to: entry });
      entry = key;
    }
    entryOf.set(ending.result.id, entry);
  }
  /* どの結末の印にも当たらない変更の業務ルールも、作用として図に残す。 */
  for (const rule of mutations) {
    addNode({
      key: logicRuleKey(rule.ruleId),
      kind: 'effect',
      title: name(rule),
      lines: [],
      lane: DRAFT_LANE,
      ruleId: rule.ruleId,
    });
  }

  /* 分岐。結末を決める判定表の列ごとに 1 つ、行を上から順に線にする。 */
  if (deciding !== null && deciding.body.kind === 'decisionTable') {
    const decidingBody = deciding.body;
    const columns = decidingBody.inputs;
    for (const column of columns) {
      const key = branchKey(column.id);
      /* 分岐に畳んだ業務ルールは、その業務ルールが読む値を分岐の入力にし、押すとその業務ルールを開く。 */
      const fold = folded.get(column.id);
      addNode({
        key,
        kind: 'branch',
        title: name(column),
        lines: [],
        lane: DRAFT_LANE,
        ruleId: fold === undefined ? deciding.ruleId : fold.ruleId,
      });
      connectReads(key, fold === undefined ? [column] : fold.inputs.map((input) => input.term));
    }
    /*
     * 分岐の先の入口。次の分岐が読む値を業務の中で作るなら、その値を作る業務ルールの連なりの先頭へ
     * 渡す（図の「選定途中のユーザーがいるか →いない→ 選定可能なユーザーの判定をする」）。作らなければ
     * 次の分岐そのもの。
     */
    const entryOfColumn = (termId: number): string => {
      let current = producers.get(termId);
      if (current === undefined) return branchKey(termId);
      const visited = new Set<BusinessRuleView>();
      while (!visited.has(current)) {
        visited.add(current);
        const upstream: BusinessRuleView | undefined = current.inputs
          .map((input) => producers.get(input.term.id))
          .find((rule) => rule !== undefined && !visited.has(rule));
        if (upstream === undefined) break;
        current = upstream;
      }
      return logicRuleKey(current.ruleId);
    };
    /*
     * どの行にも当たらなかった流れのラベル。分岐に畳んだ判定表なら、その判定表が出す値のうち結末を
     * 決める判定表が見ない値（「いる」だけを見るなら「いない」）。それ以外は「それ以外」。
     */
    const otherwiseOf = (columnIndex: number, termId: number): string => {
      const fold = folded.get(termId);
      if (fold?.body.kind !== 'decisionTable') return texts.otherwise;
      const tested = new Set(
        reachableRowsOf(decidingBody).flatMap((row) => {
          const cell = row.cells[columnIndex];
          return cell !== undefined && cell !== null && 'id' in cell ? [cell.id] : [];
        }),
      );
      const rest = new Map<number, string>();
      for (const row of fold.body.rows) {
        for (const output of row.outputs)
          if (!tested.has(output.id)) rest.set(output.id, name(output));
      }
      return rest.size === 0 ? texts.otherwise : [...rest.values()].join(' ／ ');
    };
    /*
     * 業務の出力を作る業務ルールの連なり。出力をどの業務ルールも分岐も読まない業務ルール（業務の出力）
     * から読む値を遡り、分岐の値（termId）を直に読む業務ルールを先頭にする。分岐の値を読まない連なりは
     * その分岐に続かないので null。
     */
    const decidingColumns = new Set(columns.map((column) => column.id));
    const outputChainOf = (termId: number): { head: string; tail: string } | null => {
      for (const last of workers) {
        const output = last.output;
        if (output === null || decidingColumns.has(output.id)) continue;
        const read = logic.rules.some(
          (other) => other !== last && other.inputs.some((input) => input.term.id === output.id),
        );
        if (read) continue;
        const queue: BusinessRuleView[] = [last];
        const seenRules = new Set<BusinessRuleView>();
        for (let rule = queue.shift(); rule !== undefined; rule = queue.shift()) {
          if (seenRules.has(rule)) continue;
          seenRules.add(rule);
          if (rule.inputs.some((input) => input.term.id === termId)) {
            return { head: logicRuleKey(rule.ruleId), tail: outputKey(output.id) };
          }
          queue.push(
            ...rule.inputs.flatMap((input) => {
              const upstream = producers.get(input.term.id);
              return upstream === undefined ? [] : [upstream];
            }),
          );
        }
      }
      return null;
    };
    /* 分岐から行き先へ線を引く。業務の出力の連なりを通すなら、連なりの先頭へ繋ぎ、出力から行き先へ繋ぐ。 */
    const linkBranch = (
      from: string,
      label: string,
      to: string,
      through: { head: string; tail: string } | null,
    ): void => {
      if (through === null) {
        addEdge({ from, to, label });
        return;
      }
      addEdge({ from, to: through.head, label });
      addEdge({ from: through.tail, to });
    };
    const lastColumn = columns[columns.length - 1];
    const lastBranch = lastColumn === undefined ? null : branchKey(lastColumn.id);
    for (const row of reachableRowsOf(deciding.body)) {
      const tested = row.cells
        .map((cell, index) => ({ cell, column: columns[index] }))
        .filter(
          (entry): entry is { cell: NonNullable<DecisionTableCellView>; column: NamedRef } =>
            entry.cell !== null && entry.column !== undefined,
        );
      /* 行の出力のうち結末の値に当たるものの入口（無ければ結末に繋がらない行なので描かない）。 */
      const result = row.outputs.find((output) => entryOf.has(output.id));
      const target = result === undefined ? undefined : entryOf.get(result.id);
      if (result === undefined || target === undefined) continue;
      /* どの列も見ない行は、最後の分岐から「それ以外」で行き先へ渡す。 */
      if (tested.length === 0 && lastBranch !== null) {
        addEdge({ from: lastBranch, to: target, label: texts.otherwise });
      }
      if (tested.length === 0) continue;
      for (const [index, entry] of tested.entries()) {
        const next = tested[index + 1];
        const label = cellText(entry.cell, texts);
        /*
         * 業務の出力を返す結末へ向かう線は、この分岐の値を読んで業務の出力を作る業務ルールの連なりを
         * 通す（図の「選定可能なユーザーリストの数 →1 件以上→ 並べる → 上位 1 件を取得する → 作用」）。
         */
        const through =
          next === undefined && !errorResults.has(result.id)
            ? outputChainOf(entry.column.id)
            : null;
        linkBranch(
          branchKey(entry.column.id),
          label,
          next === undefined ? target : entryOfColumn(next.column.id),
          through,
        );
      }
    }
    /* どの行にも当たらなかった流れは、次の列の分岐へ渡す。 */
    for (const [index, column] of columns.entries()) {
      const next = columns[index + 1];
      if (next === undefined) continue;
      addEdge({
        from: branchKey(column.id),
        to: entryOfColumn(next.id),
        label: otherwiseOf(index, column.id),
      });
    }
  }

  const lanes = flowLanesOf(nodes, edges);
  return { nodes: nodes.map((node) => ({ ...node, lane: getOr(lanes, node.key, 0) })), edges };
}
